import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path, opts) =>
  fetch(path, { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, ...opts });

export default function OwnerIntroducers() {
  const [introducers, setIntroducers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterAgreement, setFilterAgreement] = useState('');
  const [waiveModal, setWaiveModal] = useState(null); // { id, name }
  const [waiveNote, setWaiveNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search)           params.set('search', search);
    if (filterCompany)    params.set('company_id', filterCompany);
    if (filterAgreement)  params.set('agreement_status', filterAgreement);
    const res = await API(`/api/owner/introducers?${params}`);
    const data = await res.json();
    if (!res.ok) { setError(data.error || 'Failed'); setLoading(false); return; }
    setIntroducers(data.introducers || []);
    const uniqueCompanies = [...new Map(
      (data.introducers || []).map(i => [i.company_id, { id: i.company_id, name: i.company_name }])
    ).values()];
    setCompanies(uniqueCompanies);
    setLoading(false);
  }, [search, filterCompany, filterAgreement]);

  useEffect(() => { load(); }, [load]);

  const waiveAgreement = async () => {
    setSaving(true);
    const res = await API(`/api/owner/introducers/${waiveModal.id}/waive-agreement`, {
      method: 'PATCH',
      body: JSON.stringify({ note: waiveNote }),
    });
    setSaving(false);
    if (res.ok) { setWaiveModal(null); setWaiveNote(''); load(); }
    else { const d = await res.json(); setError(d.error || 'Failed to waive'); }
  };

  const reinstateAgreement = async (id) => {
    if (!window.confirm('Re-enable agreement requirement for this introducer?')) return;
    const res = await API(`/api/owner/introducers/${id}/require-agreement`, { method: 'PATCH' });
    if (res.ok) load();
    else { const d = await res.json(); setError(d.error || 'Failed'); }
  };

  const agreementBadge = (i) => {
    if (!i.agreement_required) return <span style={badge('purple')}>Waived</span>;
    if (i.agreement_status === 'approved') return <span style={badge('green')}>Signed</span>;
    if (i.agreement_id) return <span style={badge('amber')}>Pending Review</span>;
    return <span style={badge('red')}>Not Started</span>;
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Introducers</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>All introducers across every broker company</p>

        {error && <div style={alertStyle('red')}>{error}<button onClick={() => setError('')} style={{ float: 'right', border: 'none', background: 'none', cursor: 'pointer' }}>✕</button></div>}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            placeholder="Search name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={inputStyle}
          />
          <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={inputStyle}>
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterAgreement} onChange={e => setFilterAgreement(e.target.value)} style={inputStyle}>
            <option value="">All Agreement Status</option>
            <option value="signed">Signed</option>
            <option value="pending">Pending</option>
            <option value="waived">Waived</option>
          </select>
        </div>

        {/* Summary row */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Total', value: introducers.length, color: '#3b82f6' },
            { label: 'Signed', value: introducers.filter(i => i.agreement_required && i.agreement_status === 'approved').length, color: '#10b981' },
            { label: 'Pending', value: introducers.filter(i => i.agreement_required && i.agreement_status !== 'approved').length, color: '#f59e0b' },
            { label: 'Waived', value: introducers.filter(i => !i.agreement_required).length, color: '#8b5cf6' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: '8px', padding: '16px 24px', borderLeft: `4px solid ${s.color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Introducer', 'Company', 'Type', 'Fee', 'Agreement', 'Deals', 'Commission', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {introducers.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>No introducers found</td></tr>
                ) : introducers.map(i => (
                  <tr key={i.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{i.name}</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>{i.contact_email}</div>
                    </td>
                    <td style={td}>{i.company_name}</td>
                    <td style={td}><span style={{ textTransform: 'capitalize' }}>{i.type}</span></td>
                    <td style={td}>
                      {i.fee_type === 'fixed'
                        ? `£${parseFloat(i.fixed_fee || 0).toLocaleString()}`
                        : i.fee_type === 'percentage'
                          ? `${i.fee_percentage}%`
                          : '—'}
                    </td>
                    <td style={td}>{agreementBadge(i)}</td>
                    <td style={td}>{i.deal_count}</td>
                    <td style={td}>£{parseFloat(i.total_commission || 0).toLocaleString()}</td>
                    <td style={td}>
                      {/* Waive/Reinstate hidden 10 July 2026 — leftover from when brokers
                          managed their own introducer agreements directly. Since 7 July 2026
                          template upload/mapping/approval moved to platform-owner-only
                          (broker_companies.introducer_agreement_workflow_enabled +
                          active_agreement_template_id), this per-row toggle no longer maps to
                          a real broker workflow and was firing on introducers with no agreement
                          relationship at all. Re-enable (and redesign per the "send agreement
                          for signing" / "send updated agreement" discussion) if/when agreement
                          upload+build moves back into the broker portal. See TODO.md §P0.6. */}
                      {'—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Waive modal */}
        {waiveModal && (
          <div style={modalOverlay}>
            <div style={modalBox}>
              <h3 style={{ marginTop: 0 }}>Waive Agreement Requirement</h3>
              <p style={{ color: '#374151' }}>
                Remove the system agreement requirement for <strong>{waiveModal.name}</strong>.
                They will be able to access the platform without signing an agreement.
              </p>
              <label style={labelStyle}>Reason / Note (optional)</label>
              <textarea
                value={waiveNote}
                onChange={e => setWaiveNote(e.target.value)}
                rows={3}
                placeholder="e.g. Existing agreement held offline"
                style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end' }}>
                <button onClick={() => setWaiveModal(null)} style={btnSm('slate')}>Cancel</button>
                <button onClick={waiveAgreement} disabled={saving} style={btnSm('purple')}>
                  {saving ? 'Saving…' : 'Confirm Waiver'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: '#fff' };
const td = { padding: '12px 16px', fontSize: '14px', color: '#374151', verticalAlign: 'middle' };
const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalBox = { background: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '480px', width: '90%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' };

const badge = (color) => {
  const map = { green: ['#d1fae5','#065f46'], amber: ['#fef3c7','#92400e'], red: ['#fee2e2','#991b1b'], purple: ['#ede9fe','#5b21b6'], slate: ['#f1f5f9','#334155'] };
  const [bg, text] = map[color] || map.slate;
  return { background: bg, color: text, padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600 };
};

const btnSm = (color) => {
  const map = { purple: '#7c3aed', slate: '#64748b', red: '#dc2626', green: '#059669' };
  return { background: map[color] || '#64748b', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', fontWeight: 500 };
};

const alertStyle = (color) => ({
  background: color === 'red' ? '#fee2e2' : '#d1fae5',
  color: color === 'red' ? '#991b1b' : '#065f46',
  padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px'
});
